// src/components/DownloadModal.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TbCrown, TbDownload, TbCheck, TbX, TbSparkles, TbShieldCheck } from 'react-icons/tb';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { 
  detectUserCountry, 
  getCurrencyForCountry,
  getPriceConfig
} from '../util/domain-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const DownloadModal = ({ 
  isOpen, 
  closeModal, 
  selectedTemplate, 
  onDownload, 
  freeTemplates = ['Professional', 'Classic', 'Dynamic', 'Modern']
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [selectedPlan, setSelectedPlan] = useState('lifetime');
  const [currency, setCurrency] = useState('inr');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [razorpayReady, setRazorpayReady] = useState(false);

  // Check if selected template is in the free templates list
  const isTemplateAvailable = freeTemplates.includes(selectedTemplate);

  // Detect user's country and set currency
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const countryCode = await detectUserCountry();
        const detectedCurrency = getCurrencyForCountry(countryCode);
        setCurrency(detectedCurrency);
      } catch (error) {
        console.error('Error detecting country:', error);
        setCurrency('inr');
      }
    };
    
    detectCountry();
  }, []);

  // Load Razorpay script when currency is INR
  useEffect(() => {
    if (currency.toLowerCase() === 'inr') {
      const loadRazorpayScript = () => {
        return new Promise((resolve, reject) => {
          if (window.Razorpay) {
            setRazorpayReady(true);
            return resolve();
          }
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          script.onload = () => {
            setRazorpayReady(true);
            resolve();
          };
          script.onerror = reject;
          document.body.appendChild(script);
        });
      };

      loadRazorpayScript().catch(err => {
        console.error('Error loading Razorpay script:', err);
      });
    }
  }, [currency]);

  // Plan data
  const planOptions = [
    {
      id: 'sevenDays',
      name: '7 Days',
      badge: null,
      description: '7-day access'
    },
    {
      id: 'oneMonth',
      name: '1 Month',
      badge: null,
      description: '30-day access'
    },
    {
      id: 'lifetime',
      name: 'Lifetime',
      badge: 'Best Value',
      description: 'Pay once, keep forever'
    }
  ];

  const currentPriceConfig = getPriceConfig(selectedPlan, currency);

  // Handle direct checkout or navigation
  const handleUpgrade = async () => {
    setErrorMessage('');
    
    // If not logged in, redirect to login with state
    if (!user || !user.email) {
      closeModal();
      localStorage.setItem('selectedPlan', selectedPlan);
      localStorage.setItem('selectedCurrency', currency);
      navigate('/login?redirect=plans');
      return;
    }

    setLoading(true);
    const domainName = 'cvdesigner.ai';
    const userId = user.uid;

    try {
      if (currency.toLowerCase() === 'inr') {
        // Razorpay Checkout
        if (!window.Razorpay) {
          throw new Error('Payment gateway is loading. Please try again in a moment.');
        }

        const response = await axios.post(`${BACKEND_URL}/api/create-razorpay-order`, {
          planId: selectedPlan,
          userId,
          email: user.email,
          name: user.displayName || user.email,
          domainName
        });

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to create order');
        }

        const { order, key_id, user: userData, config } = response.data;

        const options = {
          key: key_id,
          amount: order.amount,
          currency: order.currency,
          name: config.display.name,
          description: config.display.description,
          image: config.display.logo,
          order_id: order.id,
          handler: async function(razorpayRes) {
            try {
              const verifyResponse = await axios.post(`${BACKEND_URL}/api/verify-razorpay-payment`, {
                razorpay_payment_id: razorpayRes.razorpay_payment_id,
                razorpay_order_id: razorpayRes.razorpay_order_id,
                razorpay_signature: razorpayRes.razorpay_signature,
                userId,
                planId: selectedPlan,
                email: user.email,
                domainName
              });

              if (verifyResponse.data.success) {
                closeModal();
                navigate(`/success?payment_id=${razorpayRes.razorpay_payment_id}`);
              } else {
                throw new Error(verifyResponse.data.message || 'Payment verification failed');
              }
            } catch (err) {
              console.error('Error verifying Razorpay payment:', err);
              setErrorMessage(err.message || 'Payment verification failed');
            } finally {
              setLoading(false);
            }
          },
          prefill: {
            name: userData.name,
            email: userData.email,
            contact: userData.contact || ''
          },
          notes: {
            userId,
            planId: selectedPlan,
            domainName
          },
          theme: {
            color: config.theme.color || '#4f46e5'
          },
          modal: {
            ondismiss: function() {
              setLoading(false);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
        razorpay.on('payment.failed', function(failedRes) {
          setErrorMessage(failedRes.error?.description || 'Payment failed. Please try again.');
          setLoading(false);
        });

      } else {
        // Stripe Checkout (USD)
        let customerId;
        const userDocRef = doc(db, 'users', user.email);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists() && userDoc.data().customerId) {
          customerId = userDoc.data().customerId;
        } else {
          const createCustomerResponse = await axios.post(`${BACKEND_URL}/api/create-customer`, {
            email: user.email,
            name: user.displayName || user.email,
            userId
          });

          if (createCustomerResponse.data.success) {
            customerId = createCustomerResponse.data.customerId;
            await setDoc(userDocRef, {
              email: user.email,
              customerId,
              userId,
              createdAt: new Date()
            }, { merge: true });
          } else {
            throw new Error('Failed to setup customer account');
          }
        }

        const response = await axios.post(`${BACKEND_URL}/api/create-checkout-session`, {
          planId: selectedPlan,
          customerId,
          currency,
          email: user.email,
          userId
        });

        if (response.data.success && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error(response.data.message || 'Could not initiate checkout');
        }
      }
    } catch (error) {
      console.error('Checkout error in modal:', error);
      setErrorMessage(error.message || 'Could not start checkout. Redirecting to plans page...');
      setTimeout(() => {
        closeModal();
        navigate('/plans', { state: { selectedPlan } });
      }, 1200);
      setLoading(false);
    }
  };

  const handleNavigateToPlans = () => {
    closeModal();
    navigate('/plans', { state: { selectedPlan } });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all border border-gray-100">
                {isTemplateAvailable ? (
                  // Free Template Content
                  <>
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                      <Dialog.Title as="h3" className="text-xl font-bold text-gray-900 flex items-center">
                        <TbDownload className="mr-2 text-indigo-600 text-2xl" />
                        Download Your Document
                      </Dialog.Title>
                      <button 
                        onClick={closeModal} 
                        className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <TbX size={20} />
                      </button>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-600 mb-6">
                        You're downloading the <span className="font-semibold text-gray-900">{selectedTemplate}</span> template.
                      </p>
                      
                      <div className="flex space-x-3 justify-end">
                        <button
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none transition-colors"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none shadow-sm transition-colors"
                          onClick={onDownload}
                        >
                          Download Now
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Enhanced Premium Template Content with Plan Selection
                  <>
                    {/* Header Banner */}
                    <div className="relative bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 p-6 text-white overflow-hidden">
                      {/* Decorative background glow */}
                      <div className="absolute -right-10 -top-10 w-36 h-36 bg-white/10 rounded-full blur-xl pointer-events-none" />
                      
                      <div className="flex items-start justify-between relative z-10">
                        <div className="flex items-center space-x-3.5">
                          <div className="w-12 h-12 bg-amber-400 text-amber-950 rounded-2xl flex items-center justify-center shadow-md font-bold text-2xl">
                            <TbCrown />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs uppercase tracking-wider font-extrabold bg-white/20 px-2 py-0.5 rounded-full">
                                Premium Template
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-white mt-1">
                              Unlock {selectedTemplate || 'Premium'}
                            </h3>
                            <p className="text-indigo-100 text-xs mt-0.5">
                              Upgrade to download and use this exclusive ATS-optimized design
                            </p>
                          </div>
                        </div>

                        <button 
                          onClick={closeModal}
                          className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-1.5 rounded-lg transition-colors"
                          aria-label="Close"
                        >
                          <TbX size={18} />
                        </button>
                      </div>

                      {/* Currency Toggle */}
                      <div className="mt-4 flex items-center justify-between pt-3 border-t border-white/15 text-xs">
                        <span className="text-indigo-100 font-medium">Select a plan to continue:</span>
                        <div className="inline-flex rounded-lg bg-black/20 p-0.5">
                          <button
                            type="button"
                            onClick={() => setCurrency('inr')}
                            className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                              currency === 'inr' 
                                ? 'bg-white text-indigo-700 shadow-sm' 
                                : 'text-white/80 hover:text-white'
                            }`}
                          >
                            ₹ INR
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrency('usd')}
                            className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                              currency === 'usd' 
                                ? 'bg-white text-indigo-700 shadow-sm' 
                                : 'text-white/80 hover:text-white'
                            }`}
                          >
                            $ USD
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      {errorMessage && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center">
                          <span className="font-semibold mr-1.5">Error:</span> {errorMessage}
                        </div>
                      )}

                      {/* Plan Options */}
                      <div className="grid grid-cols-3 gap-2.5 mb-5">
                        {planOptions.map((plan) => {
                          const isSelected = selectedPlan === plan.id;
                          const priceInfo = getPriceConfig(plan.id, currency);

                          return (
                            <div
                              key={plan.id}
                              onClick={() => setSelectedPlan(plan.id)}
                              className={`relative cursor-pointer rounded-xl p-3 border-2 text-center transition-all flex flex-col justify-between ${
                                isSelected
                                  ? 'border-indigo-600 bg-indigo-50/50 shadow-md ring-2 ring-indigo-200/50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50/50'
                              }`}
                            >
                              {plan.badge && (
                                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                                  {plan.badge}
                                </span>
                              )}

                              <div>
                                <h4 className="font-bold text-gray-900 text-sm mt-0.5">
                                  {plan.name}
                                </h4>
                                <div className="my-1.5">
                                  <span className="text-lg font-black text-indigo-600">
                                    {priceInfo.formatted}
                                  </span>
                                </div>
                              </div>

                              <p className="text-[11px] text-gray-500 font-medium leading-tight">
                                {plan.description}
                              </p>

                              <div className="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-center">
                                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${
                                  isSelected 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'border border-gray-300'
                                }`}>
                                  {isSelected && <TbCheck />}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Benefits Checklist */}
                      <div className="bg-gray-50/80 rounded-xl p-3.5 mb-5 border border-gray-100">
                        <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2.5 flex items-center">
                          <TbSparkles className="text-indigo-600 mr-1.5 text-sm" />
                          Included with your upgrade:
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                          <div className="flex items-center space-x-1.5">
                            <TbCheck className="text-green-600 shrink-0 font-bold" />
                            <span className="font-medium">Unlimited Downloads</span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <TbCheck className="text-green-600 shrink-0 font-bold" />
                            <span className="font-medium">No Watermarks</span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <TbCheck className="text-green-600 shrink-0 font-bold" />
                            <span className="font-medium">All 22+ ATS Templates</span>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <TbCheck className="text-green-600 shrink-0 font-bold" />
                            <span className="font-medium">Full AI Resume Writer</span>
                          </div>
                        </div>
                      </div>

                      {/* Upgrade CTA Button */}
                      <button
                        type="button"
                        disabled={loading}
                        onClick={handleUpgrade}
                        className="w-full py-3 px-6 bg-gradient-to-r from-indigo-600 via-indigo-700 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-200 flex items-center justify-center space-x-2 text-base disabled:opacity-60 disabled:cursor-not-allowed group"
                      >
                        {loading ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>Processing...</span>
                          </div>
                        ) : (
                          <>
                            <TbCrown className="text-xl text-amber-300 group-hover:scale-110 transition-transform" />
                            <span>
                              Upgrade Now • {currentPriceConfig.formatted}
                            </span>
                          </>
                        )}
                      </button>

                      {/* Guarantee and links */}
                      <div className="mt-3.5 text-center space-y-1.5">
                        <p className="text-[11px] text-gray-500 flex items-center justify-center space-x-1">
                          <TbShieldCheck className="text-green-600 text-sm" />
                          <span>One-time payment • 7-day money back guarantee</span>
                        </p>
                        <div>
                          <button
                            type="button"
                            onClick={handleNavigateToPlans}
                            className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold underline underline-offset-2 transition-colors"
                          >
                            Compare all features on pricing page →
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DownloadModal;